import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { Link } from "modules/editor/blocks/Link";
import { Text } from "modules/editor/blocks/Text";
import type { BlockquoteProps } from "modules/editor/blocks/Blockquote";
import type { BodyProps } from "modules/editor/blocks/Body";
import type { CallToActionProps } from "modules/editor/blocks/CallToAction";
import type { EventsProps } from "modules/editor/blocks/Events";
import type { HeadingProps } from "modules/editor/blocks/Heading";
import type { IframeProps } from "modules/editor/blocks/Iframe";
import type { ImageProps } from "modules/editor/blocks/Image";
import type { ListProps } from "modules/editor/blocks/List";
import type { ParagraphProps } from "modules/editor/blocks/Paragraph";
import type { VideoProps } from "modules/editor/blocks/Video";
import { FrontendBlock, isBlockquoteBlock, isBodyBlock, isCallToActionBlock, isContentSummaryBlock, isCustomUrlBlock, isEventsBlock, isGridBlock, isHeadingBlock, isIframeBlock, isImageBlock, isLineBreakBlock, isLinkBlock, isListBlock, isMostReadBlock, isParagraphBlock, isPartnersBlock, isTextBlock, isVideoBlock, isVideoWithMetadataBlock } from "modules/editor/types";
import { GridProps } from "../Grid";
import { PartnersProps } from "modules/editor/blocks/Partners";
import { ContentSummaryFrontendBlock } from "modules/editor/blocks/ContentSummary";
import { CustomUrlBlockFrontendBlock } from "modules/editor/blocks/CustomUrlBlock";
import { MostReadProps } from "modules/editor/blocks/MostRead";
const Blockquote = dynamic<BlockquoteProps>(() => import("modules/editor/blocks/Blockquote").then(mod => mod.Blockquote));
const Body = dynamic<BodyProps>(() => import("modules/editor/blocks/Body").then(mod => mod.Body));
const CallToAction = dynamic<CallToActionProps>(() => import("modules/editor/blocks/CallToAction").then(mod => mod.CallToAction));
const ContentSummary = dynamic<ContentSummaryFrontendBlock>(() => import("modules/editor/blocks/ContentSummary").then(mod => mod.ContentSummary));
const Events = dynamic<EventsProps>(() => import("modules/editor/blocks/Events").then(mod => mod.Events));
const Grid = dynamic<GridProps>(() => import("modules/editor/blocks/Grid").then(mod => mod.Grid));
const Heading = dynamic<HeadingProps>(() => import("modules/editor/blocks/Heading").then(mod => mod.Heading));
const Iframe = dynamic<IframeProps>(() => import("modules/editor/blocks/Iframe").then(mod => mod.Iframe));
const Image = dynamic<ImageProps>(() => import("modules/editor/blocks/Image").then(mod => mod.Image));
const List = dynamic<ListProps>(() => import("modules/editor/blocks/List").then(mod => mod.List));
const Paragraph = dynamic<ParagraphProps>(() => import("modules/editor/blocks/Paragraph").then(mod => mod.Paragraph));
const Video = dynamic<VideoProps>(() => import("modules/editor/blocks/Video").then(mod => mod.Video));
const VideoWithMetadata = dynamic(() => import("modules/editor/blocks/VideoWithMetadata/VideoWithMetadata").then(mod => mod.VideoWithMetadata));
const Partners = dynamic<PartnersProps>(() => import("modules/editor/blocks/Partners").then(mod => mod.Partners));
const MostRead = dynamic<MostReadProps>(() => import("modules/editor/blocks/MostRead").then(mod => mod.MostRead));
const CustomUrlBlock = dynamic<CustomUrlBlockFrontendBlock>(() => import("modules/editor/blocks/CustomUrlBlock").then(mod => mod.CustomUrlBlock));
interface BlocksRendererProps {
  blocks: FrontendBlock[];
}
interface BlockComponentProps {
  block: FrontendBlock;
  isFirstBlock?: boolean;
}
export const BlockComponent = t0 => {
  "use memo";

  const $ = _c(56);
  const {
    block,
    isFirstBlock: t1
  } = t0;
  const isFirstBlock = t1 === undefined ? false : t1;
  if (isBlockquoteBlock(block)) {
    let t2;
    if ($[0] !== block.props || $[1] !== block.type) {
      t2 = <Blockquote {...block.props} data-testid={block.type} />;
      $[0] = block.props;
      $[1] = block.type;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  } else {
    if (isBodyBlock(block)) {
      let t2;
      if ($[3] !== block.props || $[4] !== block.type) {
        t2 = <Body {...block.props} data-testid={block.type} />;
        $[3] = block.props;
        $[4] = block.type;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      return t2;
    } else {
      if (isCallToActionBlock(block)) {
        let t2;
        if ($[6] !== block.props || $[7] !== block.type) {
          t2 = <CallToAction {...block.props} data-testid={block.type} />;
          $[6] = block.props;
          $[7] = block.type;
          $[8] = t2;
        } else {
          t2 = $[8];
        }
        return t2;
      } else {
        if (isEventsBlock(block)) {
          let t2;
          if ($[9] !== block) {
            t2 = <Events {...block} data-testid={block.type} />;
            $[9] = block;
            $[10] = t2;
          } else {
            t2 = $[10];
          }
          return t2;
        } else {
          if (isGridBlock(block)) {
            let t2;
            if ($[11] !== block || $[12] !== isFirstBlock) {
              t2 = <Grid {...block} isFirstPageGrid={isFirstBlock} data-testid={block.type} />;
              $[11] = block;
              $[12] = isFirstBlock;
              $[13] = t2;
            } else {
              t2 = $[13];
            }
            return t2;
          } else {
            if (isContentSummaryBlock(block)) {
              let t2;
              if ($[14] !== block) {
                t2 = <ContentSummary {...block} data-testid={block.type} />;
                $[14] = block;
                $[15] = t2;
              } else {
                t2 = $[15];
              }
              return t2;
            } else {
              if (isHeadingBlock(block)) {
                let t2;
                if ($[16] !== block.props || $[17] !== block.type) {
                  t2 = <Heading {...block.props} data-testid={block.type} />;
                  $[16] = block.props;
                  $[17] = block.type;
                  $[18] = t2;
                } else {
                  t2 = $[18];
                }
                return t2;
              } else {
                if (isIframeBlock(block)) {
                  let t2;
                  if ($[19] !== block.props || $[20] !== block.type) {
                    t2 = <Iframe {...block.props} data-testid={block.type} />;
                    $[19] = block.props;
                    $[20] = block.type;
                    $[21] = t2;
                  } else {
                    t2 = $[21];
                  }
                  return t2;
                } else {
                  if (isImageBlock(block)) {
                    let alt;
                    let props;
                    if ($[22] !== block.props) {
                      ({
                        alt,
                        ...props
                      } = block.props);
                      $[22] = block.props;
                      $[23] = alt;
                      $[24] = props;
                    } else {
                      alt = $[23];
                      props = $[24];
                    }
                    let t2;
                    if ($[25] !== alt || $[26] !== block.type || $[27] !== props) {
                      t2 = <Image alt={alt} {...props} data-testid={block.type} />;
                      $[25] = alt;
                      $[26] = block.type;
                      $[27] = props;
                      $[28] = t2;
                    } else {
                      t2 = $[28];
                    }
                    return t2;
                  } else {
                    if (isLinkBlock(block)) {
                      let t2;
                      if ($[29] !== block.props || $[30] !== block.type) {
                        t2 = <Link {...block.props} data-testid={block.type} />;
                        $[29] = block.props;
                        $[30] = block.type;
                        $[31] = t2;
                      } else {
                        t2 = $[31];
                      }
                      return t2;
                    } else {
                      if (isListBlock(block)) {
                        let t2;
                        if ($[32] !== block.props || $[33] !== block.type) {
                          t2 = <List {...block.props} data-testid={block.type} />;
                          $[32] = block.props;
                          $[33] = block.type;
                          $[34] = t2;
                        } else {
                          t2 = $[34];
                        }
                        return t2;
                      } else {
                        if (isParagraphBlock(block)) {
                          let t2;
                          if ($[35] !== block.props || $[36] !== block.type) {
                            t2 = <Paragraph {...block.props} data-testid={block.type} />;
                            $[35] = block.props;
                            $[36] = block.type;
                            $[37] = t2;
                          } else {
                            t2 = $[37];
                          }
                          return t2;
                        } else {
                          if (isTextBlock(block)) {
                            let t2;
                            if ($[38] !== block.props || $[39] !== block.type) {
                              t2 = <Text {...block.props} data-testid={block.type} />;
                              $[38] = block.props;
                              $[39] = block.type;
                              $[40] = t2;
                            } else {
                              t2 = $[40];
                            }
                            return t2;
                          } else {
                            if (isVideoBlock(block)) {
                              let t2;
                              if ($[41] !== block.props || $[42] !== block.type) {
                                t2 = <Video {...block.props} data-testid={block.type} />;
                                $[41] = block.props;
                                $[42] = block.type;
                                $[43] = t2;
                              } else {
                                t2 = $[43];
                              }
                              return t2;
                            } else {
                              if (isVideoWithMetadataBlock(block)) {
                                let t2;
                                if ($[44] !== block.props || $[45] !== block.type) {
                                  t2 = <VideoWithMetadata {...block.props} data-testid={block.type} />;
                                  $[44] = block.props;
                                  $[45] = block.type;
                                  $[46] = t2;
                                } else {
                                  t2 = $[46];
                                }
                                return t2;
                              } else {
                                if (isPartnersBlock(block)) {
                                  let t2;
                                  if ($[47] !== block.partners || $[48] !== block.props || $[49] !== block.type) {
                                    t2 = <Partners partners={block.partners} {...block.props} data-testid={block.type} />;
                                    $[47] = block.partners;
                                    $[48] = block.props;
                                    $[49] = block.type;
                                    $[50] = t2;
                                  } else {
                                    t2 = $[50];
                                  }
                                  return t2;
                                } else {
                                  if (isMostReadBlock(block)) {
                                    let t2;
                                    if ($[51] !== block) {
                                      t2 = <MostRead {...block} data-testid={block.type} />;
                                      $[51] = block;
                                      $[52] = t2;
                                    } else {
                                      t2 = $[52];
                                    }
                                    return t2;
                                  } else {
                                    if (isCustomUrlBlock(block)) {
                                      let t2;
                                      if ($[53] !== block) {
                                        t2 = <CustomUrlBlock {...block} data-testid={block.type} />;
                                        $[53] = block;
                                        $[54] = t2;
                                      } else {
                                        t2 = $[54];
                                      }
                                      return t2;
                                    } else {
                                      if (isLineBreakBlock(block)) {
                                        let t2;
                                        if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
                                          t2 = <br />;
                                          $[55] = t2;
                                        } else {
                                          t2 = $[55];
                                        }
                                        return t2;
                                      } else {
                                        return null;
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
};
export const BlocksRenderer = t0 => {
  "use memo";

  const $ = _c(9);
  const {
    blocks
  } = t0;
  let t1;
  if ($[0] !== blocks) {
    t1 = blocks.findIndex(_temp);
    $[0] = blocks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const firstBlockIndex = t1;
  let t2;
  if ($[2] !== blocks || $[3] !== firstBlockIndex) {
    let t3;
    if ($[5] !== firstBlockIndex) {
      t3 = (block_0, index) => <BlockComponent block={block_0} key={index} isFirstBlock={index === firstBlockIndex} />;
      $[5] = firstBlockIndex;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = blocks.map(t3);
    $[2] = blocks;
    $[3] = firstBlockIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <>{t2}</>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
function _temp(block) {
  return block != null;
}